import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { RichTextControls, StyledEditor } from '@bonusly/common-rich-text-editor';
import { Box, Button, Flex, rem, StyleProps, useDisclosure, VisuallyHidden } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useEvaluationId } from '../../hooks/use-evaluation-id';
import { useUniqueId } from '../../hooks/use-unique-id';
import { EvaluationType, FeedbackEvents, Message, User } from '../../lib';
import { FeedbackState } from '../../types/feedback';
import { DeleteDraftFeedbackMessageModal } from '../drafts/delete-draft-message-modal';
import { UserAvatar } from '../user/user-avatar';
import { useMessage } from './hooks/use-message';

interface MessageInputProps extends StyleProps {
	feedbackId: string;
	placeholder: string;
	lastSavedDraft?: Message;
	type: EvaluationType;
	variant?: 'responseModal';
	feedbackTemplate?: { [key: string]: string } | null;
	feedbackState: FeedbackState | null;
	recipient: User;
}

export const MessageInput: FC<MessageInputProps> = ({
	feedbackId,
	placeholder,
	type,
	lastSavedDraft,
	variant,
	feedbackTemplate,
	feedbackState,
	recipient,
	...rest
}) => {
	const id = useUniqueId();
	const { t } = useTranslation('feedback');
	const user = useCurrentUser();
	const { cancelRef, editor, formRef, isEditing, isSubmitting, isValid, onCancel, invalidateQueries, onSubmit } = useMessage(feedbackId, type, { placeholder });

	// Determine if in edit mode or if in response modal with draft feature flag enabled
	const editMode = isEditing || variant === 'responseModal';

	// Show thread controls only in edit mode and when not in response modal
	const threadControlsVisible = isEditing && variant !== 'responseModal';

	// Render delete action only in response modal, with a draft, and without template
	const renderDeleteAction = variant === 'responseModal' && lastSavedDraft && !feedbackTemplate;

	// Render back to template button if template is present and state still in draft
	const renderBackToTemplateAction = !!feedbackTemplate && feedbackState && feedbackState === FeedbackState.Draft;

	useEffect(() => {
		if (!lastSavedDraft || !editor || isSubmitting) return;

		// Only update the content if the feedbackId matches or remove the content
		const contentToSet = feedbackId === lastSavedDraft?.feedbackId ? lastSavedDraft.text : '';
		editor.commands.setContent(contentToSet);
	}, [lastSavedDraft, editor, isSubmitting, feedbackId]);

	const deleteModal = useDisclosure();
	const handleDeleteClick = () => {
		deleteModal.onOpen();
	};

	const handleSuccessDelete = () => {
		invalidateQueries(feedbackId);
		editor?.commands.clearContent();
	};
	const { clearId } = useEvaluationId();

	const handleFormSubmit = (event: any) => {
		event.preventDefault();

		// Destructure the submitter dataset for cleaner access
		const { submitter } = event.nativeEvent as SubmitEvent;
		const publish = submitter?.dataset.draft !== 'true';

		onSubmit({ publish, messageId: lastSavedDraft?.id });

		// Dismiss modal if publishing
		if (publish) {
			clearId();
		}
	};

	const threadFormActions = (
		<Flex alignItems="flex-end" justifyContent="flex-end" columnGap={3} w="full">
			<Button colorScheme="secondary" onClick={onCancel} ref={cancelRef} variant="ghost">
				{t('message.cancel')}
			</Button>
			<Button colorScheme="primary" isActive={isSubmitting} isDisabled={!isValid} type="submit" data-draft="false" variant="solid">
				{t('message.submit')}
			</Button>
		</Flex>
	);

	const handleBackToTemplate = () => {
		clearId();
		feedbackTemplate &&
			window.dispatchEvent(
				new CustomEvent(FeedbackEvents.OpenFeedbackTemplateModal, {
					detail: {
						recipient: recipient,
						templateQuestions: Object.entries(feedbackTemplate).map(([key]) => ({
							question: key,
							placeholder: '', // Placeholder is not persisted, we can send it back to the modal. But that is fine, as all questions should be answered.
						})),
						feedbackId: feedbackId,
						persistedAnswers: Object.values(feedbackTemplate),
					},
				})
			);
	};

	// Currently only the response modal supports the save as draft functionality
	const modalResponseFormActions = (
		<Flex justify={renderDeleteAction || renderBackToTemplateAction ? 'space-between' : 'flex-end'} w="100%" pt={6}>
			{renderDeleteAction && (
				<Button colorScheme="secondary" variant="solid" onClick={handleDeleteClick}>
					{t('feedback.response.delete_button')}
				</Button>
			)}

			{renderBackToTemplateAction && (
				<Button colorScheme="secondary" variant="alpha" onClick={handleBackToTemplate}>
					{t('feedback.response.back_to_template')}
				</Button>
			)}

			<Box>
				<Button colorScheme="secondary" isDisabled={!isValid} mr={3} variant="alpha" type="submit" data-draft="true">
					{t('feedback.response.save_as_draft')}
				</Button>
				<Button colorScheme="primary" isActive={isSubmitting} isDisabled={!isValid} type="submit" variant="solid" data-draft="false">
					{t('feedback.response.share_feedback')}
				</Button>
			</Box>
		</Flex>
	);

	return (
		<>
			<Flex flexDir="column" as="form" onSubmit={handleFormSubmit} ref={formRef}>
				<Flex columnGap={4} {...rest} w="100%">
					<Box h={rem(40)}>
						<UserAvatar userId={user.id} />
					</Box>

					<Flex
						bg="bg.canvas"
						border="1px solid"
						borderColor="white"
						borderRadius="md"
						direction="column"
						flexGrow={1}
						p={1}
						rowGap={2}
						w="100%"
						{...(editMode && { borderColor: 'border.input', bg: 'white' })}
					>
						<VisuallyHidden id={id}>{t('message.input_label')}</VisuallyHidden>

						{editMode && <RichTextControls editor={editor} />}
						<StyledEditor editor={editor} outline="none" py={1} px={2} {...(editMode && { minH: rem(100) })} />
						{threadControlsVisible && threadFormActions}
					</Flex>
				</Flex>
				{variant === 'responseModal' && modalResponseFormActions}
			</Flex>
			{renderDeleteAction && (
				<DeleteDraftFeedbackMessageModal
					feedbackMessageId={lastSavedDraft.id}
					isOpen={deleteModal.isOpen}
					onDeleteSuccess={handleSuccessDelete}
					onClose={deleteModal.onClose}
				/>
			)}
		</>
	);
};
